import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Logo } from '@/components/Logo';
import { AboutThisAppSection, AppTitleHero } from '@/components/promoContent';
import { loadTimelinePreferences } from '@/lib/timelinePreferences';
import { recallProjectLayout, recallProjectsLayout } from '@/lib/layoutMemory';
import { ProjectCard } from '@/components/ProjectCard';

/**
 * Placeholders shown while a page loads.
 *
 * The goal is a *silent* handover: when the data lands, nothing already on
 * screen should move. Three rules keep that true, and each one is here because
 * breaking it produced a visible jump:
 *
 * 1. **Build from the same containers as the real page.** Every skeleton below
 *    uses the same `Card` / padding / gap / grid classes as the component it
 *    stands in for, so its height is derived rather than guessed. A hand-picked
 *    `h-24` is right once and then rots.
 * 2. **Render genuinely static content for real.** The app hero and the "About
 *    this app" section don't depend on any fetch, so the skeleton renders them
 *    rather than a grey box the same shape. (The one exception is the mobile
 *    feedback button: it is static too, but importing it here would pull the
 *    feedback dialog into the main bundle, so it gets a same-sized box.)
 * 3. **Branch on CSS breakpoints, not `useIsMobile()`.** That hook reports
 *    desktop on its first render and corrects itself in an effect — which would
 *    swap the whole header out from under the user one frame in. `md:` matches
 *    the hook's 768px breakpoint and is right on the first paint.
 *
 * Sizes below are measured against the live pages (Chromium, 1280×900 and
 * 390×844). Where a number is load-bearing it says which real element it
 * mirrors, so the next person can re-derive it.
 *
 * Note the stock `ui/skeleton` uses `bg-muted`, which is invisible against this
 * app's gradient. These use white-alpha fills to match the glassmorphism theme.
 */
function SkeletonBar({ className }: { className?: string }) {
  return <div className={`rounded bg-white/20 ${className || ''}`} />;
}

/**
 * The shimmer lives on the *group*, not the bar.
 *
 * A page of placeholders holds ~100 bars, and one `animate-pulse` each means
 * ~100 concurrent animations running at the exact moment the device is busiest
 * — parsing the bundle, fetching, decoding. Animating the container instead
 * costs one, reads identically (opacity applies to the whole subtree), and
 * keeps every bar in phase. Any block of bars added below should sit inside one
 * of these; a bar standing alone (the header titles) may carry it itself.
 */
const PULSE = 'animate-pulse';

/**
 * Page shells, copied from the pages themselves so the placeholder paints the
 * same background — including `Project`'s solid `bg-pink-500` under the
 * gradient, and `Projects`' `overflow-x-hidden` (which suppresses a horizontal
 * scrollbar, and with it the ~15px of viewport height one would cost).
 */
const PROJECTS_PAGE_SHELL =
  'min-h-screen bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600 overflow-x-hidden';
const PROJECT_PAGE_SHELL =
  'min-h-screen bg-pink-500 bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600';

/**
 * Stands in for `FeedbackCard` (mobile only). Same glass box, same 38px height
 * — `py-2` around a `text-sm` line — without importing the feedback dialog into
 * the main bundle.
 */
const FeedbackCardBox = () => (
  <div className="w-full h-[38px] rounded-lg bg-white/10 backdrop-blur-md border border-white/20 shadow-sm" aria-hidden="true" />
);

/* ─── Projects page ─────────────────────────────────────────────────────── */

/**
 * A grid of project card placeholders, matching the real grid's columns.
 *
 * The cards are the REAL `ProjectCard` with no `project` — it renders its own
 * placeholder from the same JSX, so there is no lookalike copy here to fall out
 * of step with it. Only the create-card, which is interactive, is stood in for.
 */
function ProjectGridSkeleton({ count, withCreateCard }: { count: number; withCreateCard?: boolean }) {
  return (
    <div className={`grid gap-6 md:grid-cols-2 lg:grid-cols-3 ${PULSE}`}>
      {withCreateCard && (
        /* The real "Create New Project" card is interactive, so this stands in
           for it — but `min-h-[240px]` is what fixes its height in both, and it
           is the tallest card in the grid, so the first row lands correctly. */
        <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white border-dashed border-2">
          <CardContent className="flex flex-col items-center justify-center min-h-[240px] p-6">
            <SkeletonBar className="h-12 w-12 rounded-lg mb-4" />
            <SkeletonBar className="h-6 w-44" />
            <SkeletonBar className="h-4 w-36 mt-3" />
          </CardContent>
        </Card>
      )}
      {Array.from({ length: count }, (_, i) => <ProjectCard key={i} />)}
    </div>
  );
}

/**
 * The whole Projects page while its data loads.
 *
 * Card counts come from the previous visit (`layoutMemory`) — without them the
 * grid can only guess, and the guess is what makes the page lurch when the real
 * list arrives.
 */
export function ProjectsPageSkeleton() {
  // Read once per mount. A re-read mid-mount could return a different count and
  // resize the placeholder — a jump caused by the thing meant to prevent them.
  const [{ total, favorites }] = useState(recallProjectsLayout);
  const favoriteCount = Math.max(0, Math.min(favorites, total));
  const otherCount = Math.max(0, total - favoriteCount);

  return (
    <div className={PROJECTS_PAGE_SHELL}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-full" aria-busy="true" aria-label="Loading projects">
        {/* Mobile: same box as `MobileNav` — py-4 around a 40px row. */}
        <div className="md:hidden flex items-center justify-between w-full py-4">
          <Logo size={40} className="shrink-0 mr-3 [&_img]:shadow-[0_0_20px_4px_rgba(255,255,255,0.9)]" />
          <SkeletonBar className={`h-8 flex-1 max-w-[12rem] ${PULSE}`} />
          <div className="w-10 h-10 shrink-0" aria-hidden="true" />
        </div>

        {/* Desktop: the real hero. The sign-out / feedback column on the right
            is omitted — at ~88px it is shorter than the 128px logo, so it never
            affects the header's height. */}
        <div className="hidden md:flex justify-between items-start mb-8">
          <AppTitleHero />
        </div>

        <div className="md:hidden mb-4">
          <FeedbackCardBox />
        </div>

        {favoriteCount > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Favorite Projects</h2>
            <ProjectGridSkeleton count={favoriteCount} />
          </div>
        )}

        <div>
          {favoriteCount > 0 && (
            <h2 className="text-xl font-semibold text-white mb-4">All Projects</h2>
          )}
          <ProjectGridSkeleton count={otherCount} withCreateCard />
        </div>

        <AboutThisAppSection />
      </div>
    </div>
  );
}

/* ─── Project page ──────────────────────────────────────────────────────── */

/** Mirrors `VideoPlayer`'s card: title + Download button, 4:3 stage, controls. */
function PlayerSkeleton() {
  return (
    <Card className={`bg-white/10 backdrop-blur-md border-white/20 text-white ${PULSE}`}>
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-3">
          <SkeletonBar className="h-6 w-44" />
          {/* Download button — `size="default"` is h-10, and it (not the 24px
              title) sets the header row height on desktop. */}
          <SkeletonBar className="h-10 w-full sm:w-32 rounded-md" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="w-full max-w-2xl mx-auto space-y-4 min-w-0">
          <div className="aspect-[4/3] rounded-lg w-full bg-black/30" />
          {/* VideoPlayerControls, normal mode: progress bar (h-4), the
              time/clip-count line, then two rows of 40px buttons. */}
          <div className="space-y-3">
            <SkeletonBar className="h-4 w-full rounded-full" />
            <div className="flex items-center justify-between">
              <SkeletonBar className="h-5 w-28" />
              <SkeletonBar className="h-5 w-16" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <SkeletonBar className="h-10 w-40 rounded-md" />
                <SkeletonBar className="h-10 w-[136px] ml-auto rounded-md" />
              </div>
              <div className="flex items-center gap-2">
                <SkeletonBar className="h-10 w-28 rounded-md" />
                <SkeletonBar className="h-10 w-[136px] ml-auto rounded-md" />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Mirrors `AddToTimelineButtons`: a title over three stacked 56px buttons. */
function AddToTimelineSkeleton() {
  return (
    <Card className={`bg-white/10 backdrop-blur-md border-white/20 text-white ${PULSE}`}>
      <CardHeader>
        <SkeletonBar className="h-6 w-40" />
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3">
          <SkeletonBar className="h-14 w-full rounded-md" />
          <SkeletonBar className="h-14 w-full rounded-md" />
          <SkeletonBar className="h-14 w-full rounded-md" />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Mirrors `DateFilterPanel` — note it is a plain glass div, not a `Card`, and
 * carries its own `mt-8`. The chip widths are deliberate: they wrap onto a
 * second row at phone widths exactly as the real chips do.
 */
function FilterPanelSkeleton() {
  return (
    <div className={`bg-white/10 backdrop-blur-md border border-white/20 rounded-lg text-white mt-8 p-3 sm:p-6 space-y-3 ${PULSE}`}>
      <SkeletonBar className="h-6 w-40" />
      <div className="grid grid-cols-2 gap-2">
        <SkeletonBar className="h-10 rounded-md" />
        <SkeletonBar className="h-10 rounded-md" />
        <SkeletonBar className="h-10 rounded-md" />
        <SkeletonBar className="h-10 rounded-md" />
      </div>
      <div className="pt-2">
        <SkeletonBar className="h-4 w-24 mb-1.5" />
        <div className="flex flex-wrap gap-2">
          <SkeletonBar className="h-[30px] w-52 rounded-lg" />
          <SkeletonBar className="h-[30px] w-36 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

/**
 * Mirrors `VideoTimeline`'s card, including the card grid inside it.
 *
 * Card width and footer height per size are taken from the live timeline:
 * large 280×274 (210 preview + 64 footer), medium 140×159 (105 + 54), small
 * 100×75 (preview only — small cards have no footer). The size is a persisted
 * user preference, so the skeleton reads the same one the timeline will.
 */
function TimelineSkeleton({ itemCount }: { itemCount: number }) {
  const [{ cardSize }] = useState(loadTimelinePreferences);
  const cardWidth = cardSize === 'small'
    ? 'min(calc(33.333% - 5.33px), 100px)'
    : cardSize === 'medium' ? '140px' : '280px';
  const footerHeight = cardSize === 'small' ? 0 : cardSize === 'medium' ? 54 : 64;
  // Enough to cover what a viewport can show; beyond that a wrong count only
  // moves the scrollbar, and drawing hundreds of boxes for a 10k-item project
  // would cost more than it saves.
  const placeholders = Math.max(0, Math.min(itemCount, 24));

  return (
    <Card className={`bg-white/10 backdrop-blur-md border-white/20 text-white ${PULSE}`}>
      <CardHeader className="space-y-4 pb-3 sm:pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center justify-between gap-3">
            <SkeletonBar className="h-6 w-40" />
            {/* "Approve All" sits next to the title on mobile, in the controls
                row on desktop — same as the real header. */}
            <SkeletonBar className="h-10 w-36 rounded-md sm:hidden" />
          </div>
          <SkeletonBar className="h-5 w-56" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            <SkeletonBar className="h-9 w-[168px] rounded-md" />
            <SkeletonBar className="h-9 w-full sm:w-[280px] rounded-md" />
          </div>
          <SkeletonBar className="h-10 w-36 rounded-md ml-auto hidden sm:block" />
        </div>
      </CardHeader>
      <CardContent>
        {/* `VideoTimelineGrid`'s wrappers, not decoration: the outer padding and
            the group label add 64px above the first card, so leaving them out
            drew every placeholder 64px too high. Grouping defaults to "none",
            which still renders exactly one labelled group. */}
        <div className="pb-4">
          <div className="flex flex-col gap-6 p-0 sm:px-4 sm:pb-4">
            <div className="flex flex-col gap-3">
              <SkeletonBar className="h-5 w-32 mx-2" />
              <div className={`flex flex-wrap ${cardSize === 'small' ? 'gap-x-2 gap-y-8' : 'gap-x-4 gap-y-8'}`}>
                {Array.from({ length: placeholders }, (_, i) => (
                  <div key={i} style={{ width: cardWidth }} className="rounded-lg overflow-hidden bg-white/10">
                    <div className="aspect-[4/3] bg-black/20" />
                    {footerHeight > 0 && <div style={{ height: footerHeight }} />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * The whole project page while it loads.
 *
 * The two-column grid matters as much as the player box: on desktop the left
 * column (upload card + filters) and the right column (player) are laid out
 * together, so a single centred player — what this used to draw — put every
 * element on the page at the wrong height and at the wrong x.
 */
export function ProjectPageSkeleton({ projectId }: { projectId?: string }) {
  // Read once per mount — see the note in `ProjectsPageSkeleton`.
  const [{ items, canAdd }] = useState(() => recallProjectLayout(projectId));

  return (
    <div className={PROJECT_PAGE_SHELL}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8" aria-busy="true" aria-label="Loading project">
        {/* Mobile: `MobileNav` — home logo, title, hamburger. */}
        <div className="md:hidden flex items-center justify-between w-full py-4">
          <div className="w-10 h-10 flex items-center justify-center shrink-0">
            <Logo size={32} className="[&_img]:shadow-[0_0_20px_4px_rgba(255,255,255,0.9)]" />
          </div>
          <SkeletonBar className={`h-8 flex-1 mx-3 ${PULSE}`} />
          <div className="w-10 h-10 shrink-0" aria-hidden="true" />
        </div>

        {/* Desktop: 72px logo in a p-2 button, project title, permission pill. */}
        <div className="hidden md:flex justify-between items-start mb-8">
          <div className="flex items-center gap-4">
            <div className="p-2">
              <Logo size={72} className="[&_img]:shadow-[0_0_40px_8px_rgba(255,255,255,0.95)]" />
            </div>
            <div className={PULSE}>
              <SkeletonBar className="h-10 w-72 mb-2" />
              <SkeletonBar className="h-6 w-28 rounded-full" />
            </div>
          </div>
        </div>

        <div className="md:hidden mb-4">
          <FeedbackCardBox />
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          <div className="order-2 lg:order-1">
            {canAdd && <AddToTimelineSkeleton />}
            {items > 0 && <FilterPanelSkeleton />}
          </div>
          <div className="order-1 lg:order-2">
            <PlayerSkeleton />
          </div>
        </div>

        <TimelineSkeleton itemCount={items} />
      </div>
    </div>
  );
}

/* ─── Route-level fallback ──────────────────────────────────────────────── */

/**
 * The neutral placeholder: a centred "Loading…" on the app gradient.
 *
 * For routes with no skeleton of their own, and for a visitor whose session
 * hasn't resolved yet — showing them a projects skeleton before a login form
 * would be a worse jump than this.
 */
export function PlainPageFallback() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600 flex items-center justify-center">
      <div className="text-white text-xl">Loading...</div>
    </div>
  );
}

/**
 * The placeholder to show before a route's code or session has resolved.
 *
 * Used by the lazy-route `Suspense` fallback and by the app-boot auth check, so
 * a cold load goes straight to the page's own skeleton instead of stepping
 * through a centred "Loading…" first. Routes without a skeleton keep that
 * plain fallback.
 */
export function RouteSkeleton({ pathname }: { pathname: string }) {
  const projectMatch = /^\/project\/([^/]+)/.exec(pathname);
  if (projectMatch) return <ProjectPageSkeleton projectId={projectMatch[1]} />;
  if (pathname === '/' || pathname.startsWith('/projects')) return <ProjectsPageSkeleton />;

  return <PlainPageFallback />;
}
